// client/src/components/SearchAutocomplete.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, InputBase, IconButton, Popper, Paper, ClickAwayListener,
  List, ListItemButton, ListItemText, ListItemAvatar, Avatar, Typography, Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import { formatCurrency } from '../utils/currency';
import { API_BASE_URL, getProductImageUrl } from '../utils/media';
import { useStore } from '../context/StoreContext';

const RECENT_SEARCHES_KEY = 'recentSearches';
const getRecentSearches = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY)) || [];
  } catch {
    return [];
  }
};
const saveRecentSearch = (term) => {
  const existing = getRecentSearches().filter((t) => t.toLowerCase() !== term.toLowerCase());
  const updated = [term, ...existing].slice(0, 5);
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
  return updated;
};

const SearchAutocomplete = ({ sx }) => {
  const navigate = useNavigate();
  const { store } = useStore();
  const [searchParams] = useSearchParams();
  const anchorRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState(getRecentSearches());

  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/products/suggest?q=${encodeURIComponent(q)}`);
      if (response.ok) setSuggestions(await response.json());
    } catch {
      // Silently ignore — autocomplete is a nice-to-have, not critical path.
    }
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => fetchSuggestions(searchTerm), 250);
    return () => clearTimeout(handle);
  }, [searchTerm, fetchSuggestions]);

  // A plain global search — category browsing (with subcategories) is handled
  // by the picker to the left instead, so this box doesn't also try to scope
  // by category.
  const runSearch = (term) => {
    const params = new URLSearchParams();
    if (term.trim()) params.set('search', term.trim());
    if (term.trim()) setRecentSearches(saveRecentSearch(term.trim()));
    navigate(`/?${params.toString()}`);
    setOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    runSearch(searchTerm);
  };

  const handleSuggestionClick = (product) => {
    setOpen(false);
    navigate(`/products/${product.id}`);
  };

  const showDropdown = open && (suggestions.length > 0 || (searchTerm.trim().length === 0 && recentSearches.length > 0));

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: 'relative', ...sx }}>
        <Box
          ref={anchorRef}
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex', alignItems: 'center', width: '100%',
            border: 1, borderColor: 'divider', overflow: 'hidden', bgcolor: 'action.hover',
            transition: 'border-color 0.15s ease',
            '&:focus-within': { borderColor: 'primary.main', bgcolor: 'background.paper' },
          }}
        >
          <InputBase
            placeholder={`Search ${store.name}...`}
            value={searchTerm}
            onFocus={() => setOpen(true)}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1, px: 1.5, fontSize: '0.95rem' }}
          />
          <IconButton type="submit" aria-label="Search" sx={{ borderRadius: 0, px: 2, bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } }}>
            <SearchIcon fontSize="small" />
          </IconButton>
        </Box>

        <Popper open={showDropdown} anchorEl={anchorRef.current} placement="bottom-start" style={{ width: anchorRef.current?.offsetWidth, zIndex: 1300 }}>
          <Paper elevation={4} sx={{ mt: 0.5, maxHeight: 400, overflowY: 'auto' }}>
            {searchTerm.trim().length === 0 && recentSearches.length > 0 && (
              <List dense subheader={<Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, display: 'block' }}>Recent Searches</Typography>}>
                {recentSearches.map((term) => (
                  <ListItemButton key={term} onClick={() => { setSearchTerm(term); runSearch(term); }}>
                    <HistoryIcon fontSize="small" sx={{ mr: 1.5, color: 'text.disabled' }} />
                    <ListItemText primary={term} />
                  </ListItemButton>
                ))}
              </List>
            )}
            {suggestions.length > 0 && (
              <List dense>
                {suggestions.map((product) => (
                  <ListItemButton key={product.id} onClick={() => handleSuggestionClick(product)}>
                    <ListItemAvatar>
                      <Avatar variant="rounded" src={getProductImageUrl(product.imageUrl)} alt={product.name} />
                    </ListItemAvatar>
                    <ListItemText primary={product.name} />
                    <Typography variant="body2" color="text.secondary">{formatCurrency(product.price)}</Typography>
                  </ListItemButton>
                ))}
                <Divider />
                <ListItemButton onClick={handleSubmit}>
                  <ListItemText primary={`See all results for "${searchTerm}"`} primaryTypographyProps={{ color: 'primary', fontWeight: 600 }} />
                </ListItemButton>
              </List>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default SearchAutocomplete;
